import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';
import { apiClient } from '@/api/axios';
import { API } from '@/api/endpoints';
import { FIRST_PAGE, getNextPageFromMeta } from '@/api/pagination';
import { STALE_TIME } from '@/api/staleTimes';
import type { ReviewPayload } from '@/components/common/ReviewDecision';
import { TEACHER_INACTIVATION_PAGE_SIZE } from '../constants/teacherInactivation.constants';
import {
  paginatedTeacherInactivationRequestsSchema,
  teacherInactivationDetailSchema,
  teacherInactivationRequestSchema,
  teacherInactivationSummarySchema,
  type PaginatedTeacherInactivationRequests,
  type TeacherInactivationDetail,
  type TeacherInactivationFilterParams,
  type TeacherInactivationListParams,
  type TeacherInactivationRequest,
  type TeacherInactivationSummary,
} from '../types/teacherInactivation.types';

export const teacherInactivationQueryKeys = {
  all: () => ['teacher-inactivations'] as const,
  lists: () => [...teacherInactivationQueryKeys.all(), 'list'] as const,
  list: (params: TeacherInactivationListParams) =>
    [...teacherInactivationQueryKeys.lists(), params] as const,
  summaries: () => [...teacherInactivationQueryKeys.all(), 'summary'] as const,
  summary: (params: TeacherInactivationFilterParams) =>
    [...teacherInactivationQueryKeys.summaries(), params] as const,
  detail: (id: number) => [...teacherInactivationQueryKeys.all(), 'detail', id] as const,
};

export const teacherInactivationListInfiniteQueryOptions = (
  params: TeacherInactivationListParams,
) =>
  infiniteQueryOptions({
    queryKey: teacherInactivationQueryKeys.list(params),
    queryFn: async ({ pageParam }): Promise<PaginatedTeacherInactivationRequests> => {
      const res = await apiClient.get(API.teacherInactivation.list, {
        params: { ...params, page: pageParam, pageSize: TEACHER_INACTIVATION_PAGE_SIZE },
      });
      return paginatedTeacherInactivationRequestsSchema.parse(res.data);
    },
    initialPageParam: FIRST_PAGE,
    getNextPageParam: getNextPageFromMeta,
    staleTime: STALE_TIME,
  });

export const teacherInactivationSummaryQueryOptions = (params: TeacherInactivationFilterParams) =>
  queryOptions({
    queryKey: teacherInactivationQueryKeys.summary(params),
    queryFn: async (): Promise<TeacherInactivationSummary> => {
      const res = await apiClient.get(API.teacherInactivation.summary, { params });
      return teacherInactivationSummarySchema.parse(res.data);
    },
    staleTime: STALE_TIME,
  });

export const teacherInactivationDetailQueryOptions = (id: number) =>
  queryOptions({
    queryKey: teacherInactivationQueryKeys.detail(id),
    queryFn: async (): Promise<TeacherInactivationDetail> => {
      const res = await apiClient.get(API.teacherInactivation.detail(id));
      return teacherInactivationDetailSchema.parse(res.data);
    },
    staleTime: STALE_TIME,
  });

export async function approveTeacherInactivation(
  id: number,
  payload: ReviewPayload,
): Promise<TeacherInactivationRequest> {
  const res = await apiClient.patch(API.teacherInactivation.approve(id), payload);
  return teacherInactivationRequestSchema.parse(res.data);
}

export async function rejectTeacherInactivation(
  id: number,
  payload: ReviewPayload,
): Promise<TeacherInactivationRequest> {
  const res = await apiClient.patch(API.teacherInactivation.reject(id), payload);
  return teacherInactivationRequestSchema.parse(res.data);
}
